import { axiosClientAdmin } from '@/lib/auth';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { Comparator, Filter, FilterGroup, FilterOperator, SortOrder } from '@/lib/utils';
import { SearchInput } from '@/lib/api/util/types';

export enum EnumWebhookType {
  Umbo = 'Umbo',
  ThreeDEye = 'ThreeDEye',
  Smtp = 'Smtp',
  Milestone = 'Milestone',
  Camect = 'Camect',
  NXVms = 'NXVms',
  OpenEye = 'OpenEye',
  HikVisionNvr = 'HikVisionNvr',
}

export interface InboundWebhookSearchDto {
  id?: string;
  received?: Date;
  type?: EnumWebhookType;
  keywords?: string;
  failure?: string;
  processedAt?: Date;
}

export interface InboundWebhooksSearched {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  totalRows?: number;
  rows?: InboundWebhookSearchDto[];
  nextPage?: number;
}

export interface InboundWebhooksSearchParams {
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: SortOrder;
  /** Free text keyword search */
  keywords?: string;
  /** Specific webhook type */
  type?: EnumWebhookType | '';
  /** Failure / status text contains */
  failure?: string;
  /** Webhook id contains */
  id?: string;
  /** Received timestamp filters (ISO string) */
  receivedFrom?: string;
  receivedTo?: string;
}

export async function searchInboundWebhooks(
  params: InboundWebhooksSearchParams
): Promise<InboundWebhooksSearched> {
  const filters: Filter[] = [];

  if (params.keywords) {
    filters.push({ field: 'keywords', value: params.keywords, action: Comparator.ILike });
  }

  if (params.type) {
    filters.push({ field: 'type', value: params.type, action: Comparator.Eq });
  }

  if (params.failure) {
    filters.push({ field: 'failure', value: params.failure, action: Comparator.ILike });
  }

  if (params.id) {
    filters.push({ field: 'id', value: params.id, action: Comparator.ILike });
  }

  if (params.receivedFrom) {
    filters.push({ field: 'received', value: params.receivedFrom, action: Comparator.GtEq });
  }

  if (params.receivedTo) {
    filters.push({ field: 'received', value: params.receivedTo, action: Comparator.LtEq });
  }

  const searchInput = new SearchInput({
    pageNumber: params.pageNumber,
    pageSize: params.pageSize,
    sortBy: params.sortBy || 'received',
    sortDir: params.sortDir ?? SortOrder.Descending,
    filterOperator: FilterOperator.And,
    filterFilters: filters,
    filterFilterGroups: filters.length
      ? [{ operator: FilterOperator.And, filters }]
      : [],
  });

  const response = await axiosClientAdmin.get<InboundWebhooksSearched>('/Monitoring/webhooks/search', {
    params: searchInput.toParams(),
  });

  const { pageNumber, pageSize } = searchInput;
  const totalRows = response.data.totalRows || 0;
  return {
    ...response.data,
    totalRows,
    nextPage: pageNumber && pageSize && totalRows > pageNumber * pageSize ? pageNumber + 1 : undefined,
  };
}

export function useInboundWebhooksQuery(
  params: InboundWebhooksSearchParams,
  options?: UseQueryOptions<InboundWebhooksSearched, Error>
) {
  return useQuery<InboundWebhooksSearched, Error>({
    queryKey: ['inbound-webhooks', params],
    queryFn: () => searchInboundWebhooks(params),
    ...options,
  });
}
